import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ShoppingCart, X, Truck, CreditCard, Wallet, Landmark } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";
import { CAT_ICON, catColor, fmt } from "@/lib/catalog";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import useMeta from "@/lib/useMeta";

export default function Cart() {
  const nav = useNavigate();
  const meta = useMeta();
  const { items, remove, clear, total } = useCart();
  const { user } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [city, setCity] = useState("");
  const [pay, setPay] = useState("cod");
  const [busy, setBusy] = useState(false);
  const [methods, setMethods] = useState([]);

  useEffect(() => {
    client.get("/payment-methods").then((r) => setMethods(r.data)).catch(() => {});
  }, []);
  const balance = Number(user?.balance || 0);

  const checkout = async () => {
    if (user === false) { toast.error("سجّل الدخول لإتمام الشراء"); nav("/auth?next=/cart"); return; }
    if (phone.trim().length < 8) return toast.error("أدخل رقم هاتف صحيح");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error("أدخل بريدًا إلكترونيًا صحيحًا");
    if (!city) return toast.error("اختر مدينة التسليم");
    setBusy(true);
    try {
      const { data } = await client.post("/orders/checkout", {
        items: items.map((i) => ({ productId: i.productId, qty: i.qty })),
        phone: phone.trim(), email: email.trim(), payMethod: pay, city,
        origin_url: window.location.origin,
      });
      if (data.payMethod === "online" && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      clear();
      nav("/success", { state: { order: { ...data, items, phone, email, city } } });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div dir="rtl">
      <div className="sticky top-0 z-20 flex items-center gap-2.5 px-3 py-3" style={{ background: "var(--k-paper)", borderBottom: "1px solid var(--k-line)" }}>
        <button data-testid="back-btn" onClick={() => nav(-1)} className="k-tap w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><ChevronRight size={20} /></button>
        <h1 className="font-head text-[17px] font-bold">سلتي</h1>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 px-8" style={{ color: "var(--k-ink-soft)" }}>
          <ShoppingCart size={44} className="mx-auto mb-3 opacity-50" />
          <p className="text-[13.5px] leading-relaxed">سلتك فارغة — تصفّح المنتجات وابدأ الشراء بالجملة</p>
          <button onClick={() => nav("/browse")} className="k-tap mt-5 px-5 py-2.5 rounded-2xl text-white text-[13px] font-bold" style={{ background: "var(--k-ink)" }}>تصفّح العروض</button>
        </div>
      ) : (
        <div className="pt-3">
          {items.map((it, idx) => {
            const Icon = CAT_ICON[it.cat] || CAT_ICON.food;
            return (
              <div key={idx} data-testid={`cart-item-${idx}`} className="flex items-center gap-3 rounded-2xl p-3 mx-4 mb-2.5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <button data-testid={`cart-remove-${idx}`} onClick={() => remove(idx)} className="k-tap p-1" style={{ color: "var(--k-ink-soft)" }}><X size={16} /></button>
                <div className="w-13 h-13 rounded-xl flex items-center justify-center text-white shrink-0" style={{ width: 52, height: 52, background: catColor(it.cat) }}><Icon size={20} /></div>
                <div className="flex-1">
                  <p className="font-head text-[13px] font-bold mb-0.5">{it.name}</p>
                  <p className="text-[11.5px]" style={{ color: "var(--k-ink-soft)" }}>{it.qty} قطعة × {fmt(it.unitPrice)} د.ل</p>
                </div>
                <div className="font-num font-bold text-[13.5px]">{fmt(it.unitPrice * it.qty)}</div>
              </div>
            );
          })}

          <div className="rounded-2xl mx-4 my-4 p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <div className="flex justify-between text-[13px] py-1.5" style={{ color: "var(--k-ink-soft)" }}><span>عدد الأصناف</span><span className="font-num">{items.length}</span></div>
            <div className="flex justify-between text-[15px] font-bold pt-2.5 mt-1.5" style={{ borderTop: "1px solid var(--k-line)" }}><span>الإجمالي</span><span className="font-num" data-testid="cart-total">{fmt(total)} د.ل</span></div>
          </div>

          <div className="rounded-2xl mx-4 mb-3 p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <h3 className="font-head text-[14px] font-bold mb-1">بيانات التواصل والتسليم</h3>
            <p className="text-[11.5px] mb-3 leading-relaxed" style={{ color: "var(--k-ink-soft)" }}>لإرسال تفاصيل الطلب وإثبات الدفع إليك.</p>
            <label className="text-[12.5px] font-semibold block mb-1.5">رقم الهاتف</label>
            <input data-testid="buyer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="+218 9x xxx xxxx" className="w-full rounded-lg px-3 py-2.5 text-[13.5px] mb-3" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
            <label className="text-[12.5px] font-semibold block mb-1.5">البريد الإلكتروني</label>
            <input data-testid="buyer-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="name@example.com" className="w-full rounded-lg px-3 py-2.5 text-[13.5px] mb-3" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
            <label className="text-[12.5px] font-semibold block mb-1.5 flex items-center gap-1"><Truck size={13} /> مدينة التسليم</label>
            <select data-testid="delivery-city" value={city} onChange={(e) => setCity(e.target.value)} className="w-full rounded-lg px-3 py-2.5 text-[13.5px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
              <option value="">اختر المدينة…</option>
              {meta.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <label data-testid="pay-cod" className="flex items-center gap-2.5 px-4 py-3 rounded-2xl mx-4 mb-2 text-[13px]" style={{ background: "var(--k-surface)", border: `1px solid ${pay === "cod" ? "var(--k-ink)" : "var(--k-line)"}` }}>
            <input type="radio" name="pay" checked={pay === "cod"} onChange={() => setPay("cod")} style={{ accentColor: "var(--k-ink)", width: 16, height: 16 }} />
            <Truck size={16} /> دفع عند الاستلام (COD)
          </label>
          <label data-testid="pay-online" className="flex items-center gap-2.5 px-4 py-3 rounded-2xl mx-4 mb-2 text-[13px]" style={{ background: "var(--k-surface)", border: `1px solid ${pay === "online" ? "var(--k-ink)" : "var(--k-line)"}` }}>
            <input type="radio" name="pay" checked={pay === "online"} onChange={() => setPay("online")} style={{ accentColor: "var(--k-ink)", width: 16, height: 16 }} />
            <CreditCard size={16} /> دفع إلكتروني عبر Stripe
          </label>

          {balance > 0 && (
            <label data-testid="pay-wallet" className="flex items-center gap-2.5 px-4 py-3 rounded-2xl mx-4 mb-2 text-[13px]" style={{ background: "var(--k-surface)", border: `1px solid ${pay === "wallet" ? "var(--k-ink)" : "var(--k-line)"}` }}>
              <input type="radio" name="pay" checked={pay === "wallet"} onChange={() => setPay("wallet")} style={{ accentColor: "var(--k-ink)", width: 16, height: 16 }} />
              <Wallet size={16} /> المحفظة — الرصيد <span className="font-num font-bold" style={{ color: "var(--k-emerald)" }}>{fmt(balance)} د.ل</span>
            </label>
          )}

          {methods.map((m) => (
            <label key={m.id} data-testid={`pay-${m.id}`} className="flex items-start gap-2.5 px-4 py-3 rounded-2xl mx-4 mb-2 text-[13px]" style={{ background: "var(--k-surface)", border: `1px solid ${pay === m.id ? "var(--k-ink)" : "var(--k-line)"}` }}>
              <input type="radio" name="pay" checked={pay === m.id} onChange={() => setPay(m.id)} style={{ accentColor: "var(--k-ink)", width: 16, height: 16, marginTop: 2 }} />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 font-bold"><Landmark size={15} /> {m.label}</div>
                {pay === m.id && m.details && <p className="text-[11.5px] mt-1 whitespace-pre-line leading-relaxed" style={{ color: "var(--k-ink-soft)" }}>{m.details}</p>}
              </div>
            </label>
          ))}

          <div className="sticky bottom-0 px-4 pt-3 pb-4" style={{ background: "linear-gradient(to top, var(--k-paper) 65%, transparent)" }}>
            <button data-testid="checkout-btn" disabled={busy} onClick={checkout} className="k-tap w-full py-3.5 rounded-2xl font-bold text-[14.5px] text-white" style={{ background: "var(--k-ink)", opacity: busy ? 0.7 : 1 }}>
              {busy ? "جارٍ المعالجة…" : "إتمام الشراء"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
